import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, GEAR_DEFINITIONS } from '../utils/constants';

const UserManagementModule = ({ onBack, currentUser }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddUserModal, setShowAddUserModal] = useState(false);
  const [showEditUserModal, setShowEditUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [error, setError] = useState('');



  // User management endpoints require admin auth. We attach the token explicitly
  // here to avoid any environment-specific issues where the global interceptor
  // might not apply (e.g., different axios instances/bundles).
  const getAuthConfig = () => {
    const token = localStorage.getItem('authToken');
    return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
  };

  // Check admin permissions
  useEffect(() => {
    if (currentUser?.rawRole !== 'admin') {
      alert('❌ Access denied. Only administrators can access user management.');
      onBack();
      return;
    }
    fetchUsers();
  }, [currentUser, onBack]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/auth/users`, getAuthConfig());
      setUsers(response.data);
      setError('');
    } catch (err) {
      console.error('Error fetching users:', err);
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (userData) => {
    try {
      await axios.post(`${API}/auth/register`, userData, getAuthConfig());
      setShowAddUserModal(false);
      fetchUsers();
      alert('✅ User added successfully!');
    } catch (err) {
      console.error('Error adding user:', err);
      alert(`❌ Error adding user: ${err.response?.data?.detail || 'Unknown error'}`);
    }
  };

  const handleEditUser = async (userId, userData) => {
    try {
      await axios.put(`${API}/auth/users/${userId}`, userData, getAuthConfig());
      setShowEditUserModal(false);
      setSelectedUser(null);
      fetchUsers();
      alert('✅ User updated successfully!');
    } catch (err) {
      console.error('Error updating user:', err);
      alert(`❌ Error updating user: ${err.response?.data?.detail || 'Unknown error'}`);
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      const action = currentStatus ? 'deactivate' : 'activate';
      if (window.confirm(`Are you sure you want to ${action} this user?`)) {
        await axios.put(`${API}/auth/users/${userId}`, { is_active: !currentStatus }, getAuthConfig());
        fetchUsers();
        alert(`✅ User ${action}d successfully!`);
      }
    } catch (err) {
      console.error('Error toggling user status:', err);
      alert(`❌ Error updating user status: ${err.response?.data?.detail || 'Unknown error'}`);
    }
  };

  const handleUnlockUser = async (userId) => {
    try {
      await axios.post(`${API}/auth/users/${userId}/unlock`, {}, getAuthConfig());
      fetchUsers();
      alert('✅ User unlocked successfully!');
    } catch (err) {
      console.error('Error unlocking user:', err);
      alert(`❌ Error unlocking user: ${err.response?.data?.detail || 'Unknown error'}`);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      if (window.confirm('⚠️ Are you sure you want to delete this user? This action cannot be undone.')) {
        await axios.delete(`${API}/auth/users/${userId}`, getAuthConfig());
        fetchUsers();
        alert('✅ User deleted successfully!');
      }
    } catch (err) {
      console.error('Error deleting user:', err);
      alert(`❌ Error deleting user: ${err.response?.data?.detail || 'Unknown error'}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white p-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center">
            <div className="text-4xl mb-4">⏳</div>
            <p className="text-gray-600">Loading users...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white p-4 sm:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
          <div className="mb-4 sm:mb-0">
            <button 
              onClick={onBack}
              className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium mb-4"
            >
              ← Back
            </button>
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-800">USER MANAGEMENT</h1>
            <p className="text-gray-600 mt-2">Manage system users and access control</p>
          </div>
          <button
            onClick={() => setShowAddUserModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors"
          >
            ➕ Add New User
          </button>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-6">
            ❌ {error}
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Last Login
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Gear Access
                  </th>

                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {users.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-blue-500 flex items-center justify-center text-white">
                            {user.full_name.charAt(0).toUpperCase()}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">{user.full_name}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        user.role === 'admin' 
                          ? 'bg-red-100 text-red-800' 
                          : 'bg-blue-100 text-blue-800'
                      }`}>
                        {user.role === 'admin' ? 'Administrator' : 'Employee'}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {user.role === 'admin' ? (
                        <span className="text-xs text-gray-500">All gears</span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {(user.allowed_gears || ['production', 'training']).map((gid) => (
                            <span key={gid} className="inline-flex px-2 py-1 text-xs rounded-full bg-emerald-100 text-emerald-800">
                              {GEAR_DEFINITIONS.find(g => g.id === gid)?.name || gid}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          user.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                        {user.is_locked && (
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-yellow-100 text-yellow-800">
                            🔒 Locked
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {user.last_login ? new Date(user.last_login).toLocaleDateString() : 'Never'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-wrap gap-2">
                        <button
                          onClick={() => {
                            setSelectedUser(user);
                            setShowEditUserModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleToggleUserStatus(user.id, user.is_active)}
                          className={user.is_active ? "text-yellow-600 hover:text-yellow-900" : "text-green-600 hover:text-green-900"}
                        >
                          {user.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        {user.is_locked && (
                          <button
                            onClick={() => handleUnlockUser(user.id)}
                            className="text-purple-600 hover:text-purple-900"
                          >
                            Unlock
                          </button>
                        )}
                        {user.email !== currentUser?.company?.email && (
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-red-600 hover:text-red-900"
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {users.length === 0 && !loading && (
          <div className="text-center py-8">
            <div className="text-4xl mb-4">👤</div>
            <p className="text-gray-600">No users found</p>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      {showAddUserModal && (
        <AddUserModal
          onClose={() => setShowAddUserModal(false)}
          onSubmit={handleAddUser}
        />
      )}

      {/* Edit User Modal */}
      {showEditUserModal && selectedUser && (
        <EditUserModal
          user={selectedUser}
          currentUser={currentUser}
          onClose={() => {
            setShowEditUserModal(false);
            setSelectedUser(null);
          }}
          onSubmit={(userData) => handleEditUser(selectedUser.id, userData)}
        />
      )}
    </div>
  );
};

// User Profile Menu Component
const UserProfileMenu = ({ currentUser, onLogout }) => {
  const [showMenu, setShowMenu] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);

  return (
    <div className="relative">
      {/* User Avatar/Menu Button */}
      <button
        onClick={() => setShowMenu(!showMenu)}
        className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 shadow-md hover:shadow-lg transition-shadow"
      >
        <div className="w-8 h-8 bg-blue-500 rounded-full flex items-center justify-center text-white font-medium">
          {currentUser?.name?.charAt(0)?.toUpperCase() || 'U'}
        </div>
        <span className="font-medium text-gray-700">{currentUser?.name || 'User'}</span>
        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Dropdown Menu */}
      {showMenu && (
        <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
          <div className="py-2">
            {/* User Info */}
            <div className="px-4 py-2 border-b border-gray-100">
              <p className="font-medium text-gray-900">{currentUser?.name || 'User'}</p>
              <p className="text-sm text-gray-500">{currentUser?.email}</p>
              <p className="text-xs text-gray-400 capitalize">{currentUser?.role || 'Employee'}</p>
            </div>

            {/* Menu Items */}
            <button
              onClick={() => {
                setShowPasswordModal(true);
                setShowMenu(false);
              }}
              className="w-full text-left px-4 py-2 text-gray-700 hover:bg-gray-50 flex items-center gap-2"
            >
              🔒 Change Password
            </button>

            <hr className="my-1" />

            <button
              onClick={() => {
                onLogout();
                setShowMenu(false);
              }}
              className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 flex items-center gap-2"
            >
              🚪 Sign Out
            </button>
          </div>
        </div>
      )}

      {/* Password Change Modal */}
      {showPasswordModal && (
        <PasswordChangeModal
          onClose={() => setShowPasswordModal(false)}
          currentUser={currentUser}
        />
      )}

      {/* Click outside to close menu */}
      {showMenu && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setShowMenu(false)}
        />
      )}
    </div>
  );
};

// Password Change Modal Component
const PasswordChangeModal = ({ onClose, currentUser }) => {
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validation
    if (!formData.current_password || !formData.new_password || !formData.confirm_password) {
      setError('Please fill in all fields');
      return;
    }

    if (formData.new_password !== formData.confirm_password) {
      setError('New passwords do not match');
      return;
    }

    if (formData.new_password.length < 8) {
      setError('New password must be at least 8 characters long');
      return;
    }

    if (formData.current_password === formData.new_password) {
      setError('New password must be different from current password');
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: formData.current_password,
        new_password: formData.new_password
      });
      
      alert('✅ Password changed successfully!');
      onClose();
    } catch (err) {
      console.error('Password change error:', err);
      setError(err.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Change Password</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          {error && (
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
              ❌ {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Current Password *
              </label>
              <input
                type="password"
                value={formData.current_password}
                onChange={(e) => setFormData(prev => ({ ...prev, current_password: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter your current password"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                New Password *
              </label>
              <input
                type="password"
                value={formData.new_password}
                onChange={(e) => setFormData(prev => ({ ...prev, new_password: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter new password (min. 8 characters)"
                minLength="8"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Confirm New Password *
              </label>
              <input
                type="password"
                value={formData.confirm_password}
                onChange={(e) => setFormData(prev => ({ ...prev, confirm_password: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Confirm new password"
                required
              />
            </div>

            <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded">
              <p className="font-medium mb-1">Password Requirements:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>At least 8 characters long</li>
                <li>Must be different from current password</li>
                <li>Use a strong, unique password</li>
              </ul>

            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Changing...' : 'Change Password'}
              </button>
            </div>
          </form>
        </div>
        </div>
      </div>
  );
};

// Add User Modal Component
const AddUserModal = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'employee',
    allowed_gears: ['production', 'training']
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.email || !formData.password || !formData.full_name) {
      alert('❌ Please fill in all required fields');
      return;
    }

    if (formData.password.length < 8) {
      alert('❌ Password must be at least 8 characters long');
      return;
    }

    if (!formData.email.includes('@')) {
      alert('❌ Please enter a valid email address');
      return;
    }

    setLoading(true);
    await onSubmit(formData);
    setLoading(false);
  };
  const handleToggleGear = (gearId) => {
    const required = ['production', 'training'].includes(gearId);
    if (required) return;

    setFormData(prev => {
      const current = Array.isArray(prev.allowed_gears) ? prev.allowed_gears : ['production', 'training'];
      if (current.includes(gearId)) {
        return { ...prev, allowed_gears: current.filter(g => g !== gearId) };
      }
      return { ...prev, allowed_gears: Array.from(new Set([...current, gearId])) };
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Add New User</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="e.g. John Smith"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="user@company.com"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Password *
              </label>
              <input
                type="password"
                value={formData.password}
                onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Minimum 8 characters"
                minLength="8"
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                Must be at least 8 characters long
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role *


            {formData.role === 'employee' && (
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="font-semibold text-gray-800 mb-2">Gear Access (Employee)</div>
                <p className="text-xs text-gray-600 mb-3">Production and Training are always included by default.</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {GEAR_DEFINITIONS.map((g) => {
                    const required = ['production', 'training'].includes(g.id);
                    const checked = required || (formData.allowed_gears || []).includes(g.id);
                    return (
                      <label key={g.id} className={`flex items-center gap-2 text-sm ${required ? 'opacity-70' : ''}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={required}
                          onChange={() => handleToggleGear(g.id)}
                        />
                        <span>{g.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

              </label>
              <select
                value={formData.role}
                onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              >
                <option value="employee">Employee</option>
                <option value="admin">Administrator</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {formData.role === 'admin' 
                  ? 'Full system access including user management'
                  : 'Access is controlled per employee using Gear Access below'
                }
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Adding...' : 'Add User'}
              </button>
            </div>
          </form>
        </div>
        </div>
      </div>
  );
};

// Edit User Modal Component
const EditUserModal = ({ user, currentUser, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    email: user.email,
    full_name: user.full_name,
    role: user.role,
    is_active: user.is_active,
    allowed_gears: user.allowed_gears || ['production', 'training']
  });

  const handleToggleGear = (gearId) => {
    const required = ['production', 'training'].includes(gearId);
    if (required) return;

    setFormData(prev => {
      const current = Array.isArray(prev.allowed_gears) ? prev.allowed_gears : ['production', 'training'];
      if (current.includes(gearId)) {
        return { ...prev, allowed_gears: current.filter(g => g !== gearId) };
      }
      return { ...prev, allowed_gears: Array.from(new Set([...current, gearId])) };
    });
  };

  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.email || !formData.full_name) {
      alert('❌ Please fill in all required fields');
      return;
    }

    if (!formData.email.includes('@')) {
      alert('❌ Please enter a valid email address');
      return;
    }

    setLoading(true);
    await onSubmit(formData);
    setLoading(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-bold text-gray-800">Edit User</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 text-2xl"
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                value={formData.full_name}
                onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />


            {formData.role === 'employee' && (
              <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                <div className="font-semibold text-gray-800 mb-2">Gear Access (Employee)</div>
                <p className="text-xs text-gray-600 mb-3">Production and Training are always included.</p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {GEAR_DEFINITIONS.map((g) => {
                    const required = ['production', 'training'].includes(g.id);
                    const checked = required || (formData.allowed_gears || []).includes(g.id);
                    return (
                      <label key={g.id} className={`flex items-center gap-2 text-sm ${required ? 'opacity-70' : ''}`}>
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={required}
                          onChange={() => handleToggleGear(g.id)}
                        />
                        <span>{g.name}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            )}

            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address *
              </label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Role *
              </label>
              <select
                value={formData.role}
                onChange={(e) => setFormData(prev => ({ ...prev, role: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                required
                disabled={user.email === currentUser?.company?.email}
              >
                <option value="employee">Employee</option>
                <option value="admin">Administrator</option>
              </select>
              {user.email === currentUser?.company?.email && (
                <p className="text-xs text-orange-600 mt-1">
                  Cannot change role of default admin user
                </p>
              )}
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="rounded border-gray-300 text-blue-600 shadow-sm focus:ring-blue-500"
                  disabled={user.email === currentUser?.company?.email}
                />
                <span className="ml-2 text-sm text-gray-700">Active User</span>
              </label>
              {user.email === currentUser?.company?.email && (
                <p className="text-xs text-orange-600 mt-1">
                  Cannot deactivate default admin user
                </p>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Updating...' : 'Update User'}
              </button>
            </div>
          </form>
        </div>
        </div>
      </div>
  );
};



// ==================== SALES MODULE ====================


export { UserManagementModule, UserProfileMenu };
